import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import type { Json } from "@/types/database";
import type { Suggestion } from "@/lib/suggestions/types";

/**
 * useSuggestions (BW18 PR-G).
 *
 * Reads `suggestion` rows for the current user, joins against
 * `suggestion_dismissal` to filter out dismissed/snoozed entries, returns
 * the active set ordered by priority.
 */

export type ActiveSuggestion = Suggestion & {
  id: string;
  fired_at: string;
};

const PRIORITY_ORDER: Record<Suggestion["priority"], number> = {
  high: 0,
  medium: 1,
  low: 2,
};

export function useSuggestions() {
  const { session } = useSession();
  const userId = session?.user?.id;

  return useQuery<ActiveSuggestion[]>({
    queryKey: ["suggestions", userId],
    enabled: Boolean(userId),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!userId) return [];
      const now = new Date().toISOString();
      const [suggestionsRes, dismissalsRes] = await Promise.all([
        supabase
          .from("suggestion")
          .select("id, rule_key, priority, title, body, action, citations, fired_at, expires_at")
          .eq("user_id", userId)
          .gt("expires_at", now)
          .order("fired_at", { ascending: false }),
        supabase
          .from("suggestion_dismissal")
          .select("rule_key, dismissed_at, snooze_until")
          .eq("user_id", userId),
      ]);
      if (suggestionsRes.error) throw suggestionsRes.error;
      if (dismissalsRes.error) throw dismissalsRes.error;

      const dismissedKeys = new Set(
        (dismissalsRes.data ?? [])
          .filter((d) => {
            // Permanently dismissed = no snooze_until, treated as a hard dismiss.
            if (d.snooze_until == null) return true;
            // Snoozed: only filter while snooze is still in effect.
            return new Date(d.snooze_until) > new Date();
          })
          .map((d) => d.rule_key),
      );

      const rows = (suggestionsRes.data ?? []).filter(
        (s) => !dismissedKeys.has(s.rule_key),
      );

      const active: ActiveSuggestion[] = rows.map((row) => ({
        id: row.id,
        rule_key: row.rule_key,
        priority: row.priority as Suggestion["priority"],
        title: row.title,
        body: row.body,
        action: (row.action as Suggestion["action"]) ?? undefined,
        citations:
          (row.citations as Suggestion["citations"] | undefined) ?? undefined,
        fired_at: row.fired_at,
        expires_at: row.expires_at,
      }));

      return active
        .sort((a, b) => PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority])
        .slice(0, 5);
    },
  });
}

export function useDismissSuggestion() {
  const qc = useQueryClient();
  const { session } = useSession();
  const userId = session?.user?.id;

  return useMutation({
    mutationFn: async (args: { rule_key: string; snooze_hours?: number }) => {
      if (!userId) throw new Error("not signed in");
      const snooze_until =
        args.snooze_hours != null
          ? new Date(Date.now() + args.snooze_hours * 3600 * 1000).toISOString()
          : null;
      const { error } = await supabase.from("suggestion_dismissal").upsert(
        {
          user_id: userId,
          rule_key: args.rule_key,
          dismissed_at: new Date().toISOString(),
          snooze_until,
        },
        { onConflict: "user_id,rule_key" },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["suggestions", userId] });
    },
  });
}

// Re-export Json for ergonomics in the engine context types.
export type { Json };
